import type { Diagnostic, Params } from '../lint/diagnostic';
import type { MessageCatalog, MessageCode, RenderedDiagnostic } from './codes';

/**
 * The English text for every code the engine can emit, and the two pure functions that turn
 * a code into a sentence.
 *
 * This file is the SINGLE source of these strings (ADR-0018). It is what makes the engine
 * usable headless — a CLI or a test can render a report without i18next, React or a DOM —
 * and it is what keeps the rest of the core language-free: a check emits
 * `{ code, span, params }` and never prose, so adding a locale cannot break a core test.
 *
 * The catalog is typed as {@link MessageCatalog}, i.e. `Readonly<Record<MessageCode, string>>`.
 * That is exhaustive BY CONSTRUCTION: adding a `DiagnosticCode` or a `RepairRuleId` fails
 * compilation here instead of showing a user the raw string `number/leading-zero`, which is
 * exactly what the legacy build shipped. `en.test.ts` proves the same thing at run time, so
 * a catalog assembled from a JSON locale file is checked too.
 *
 * ── House style for these sentences ─────────────────────────────────────────────────────
 *
 * A developer is reading this while looking at their own document, so every message:
 *
 *   - names the SPECIFIC cause. "Invalid JSON" is never an acceptable message; the whole
 *     point of a 35-code taxonomy is that we always know more than that.
 *   - quotes the offending token when the token is what is wrong (`{{text}}`, `{{key}}`,
 *     `{{token}}`), because "a number is malformed" sends the reader hunting.
 *   - says what JSON allows instead, when that is not obvious. Most of these errors come
 *     from pasting JavaScript, Python or YAML, and naming the difference is the fix.
 *   - is one complete sentence per code, never assembled from fragments — a translator must
 *     be free to reorder it (ADR-0018).
 *
 * ── Why no plurals here ─────────────────────────────────────────────────────────────────
 *
 * i18next expresses plurals as sibling keys (`…_one` / `…_other`), and `MessageCode` has no
 * room for a suffix — `fix.close-open-containers_one` is not a `FixLabelCode`. So no message
 * in this catalog takes a count. That is not a workaround: a diagnostic describes ONE
 * position and a fix label is a button, and neither has anything to count. Counts belong to
 * the chrome (`lint.status.*`, `toast.repair.*`), which is a plain i18next namespace and
 * pluralises normally.
 *
 * ── Placeholder names are the EMITTER's, not this file's ────────────────────────────────
 *
 * A placeholder is a join between two files written by two people, and nothing in the type
 * system checks it: `Params` is an open `Record<string, string | number>`, so a template
 * asking for `{{literal}}` while `checks/tokens.ts` sends `{ text }` compiles, passes both
 * files' own tests, and puts the four characters `{{li` on a user's screen.
 *
 * That is not hypothetical — it is what this catalog shipped. When the two layers were first
 * run together (2026-09-14) TWENTY of the thirty-five codes rendered with a raw `{{…}}` in
 * them: `{{literal}}` against `text` on all nine number codes, `{{key}}` against `text` on
 * `key/unquoted`, `{{found}}` against `token`, `{{escape}}` against `ch` and `text`,
 * `{{code}}` against `hex`. Every one is now named the way the check actually emits it.
 *
 * The rule that follows, and the reason the names below look less tidy than they might:
 * **when a template and a check disagree, the check wins.** The check is the producer, its
 * params are pinned by 52 golden fixtures, and a locale file is the wrong place to move a
 * contract. `analyze.test.ts` now renders every diagnostic that the whole corpus can
 * produce and fails on a surviving `{{`, so this class of defect cannot come back quietly.
 *
 * A COROLLARY, before you reach for a param that is listed as available but unused: some are
 * CONDITIONAL. `syntax/close-brace-expected` carries `{{line}}` only when the unclosed
 * opener was actually found in the token stream, so a template that interpolated it would
 * print `{{line}}` on the documents where it is missing. Unused-but-emitted params today:
 * `line` (close-brace/close-bracket-expected), `key` (colon-expected), `kind`
 * (comma-expected), `closer` (trailing-comma), `text` (single-quoted), `ch` (smart-quoted).
 */

// ── the catalog ───────────────────────────────────────────────────────────────────────────

export const EN_MESSAGES: MessageCatalog = {
  // ── syntax ──────────────────────────────────────────────────────────────────────────
  'syntax/unexpected-token': 'Unexpected {{token}} — JSON does not allow it at this position.',
  'syntax/value-expected':
    'A value is missing here. JSON expects a string, a number, an object, an array, true, false or null.',
  'syntax/property-name-expected':
    'A property name is missing here. Every member of an object starts with a double-quoted name.',
  'syntax/colon-expected': 'Missing ":" between this property name and its value.',
  'syntax/comma-expected': 'Missing "," between this value and the one before it.',
  'syntax/unexpected-comma': 'Stray "," — there is no value on one side of it.',
  'syntax/trailing-comma':
    'Trailing "," — JSON allows no comma after the last member of an object or array.',
  'syntax/close-brace-expected': 'This object is never closed: the matching "}" is missing.',
  'syntax/close-bracket-expected': 'This array is never closed: the matching "]" is missing.',
  'syntax/unexpected-eof': 'The document ends in the middle of a value.',
  'syntax/trailing-content':
    'Content after the end of the document. A JSON document holds exactly one value.',
  'syntax/empty-document': 'Nothing to check — the document is empty.',

  // ── strings ─────────────────────────────────────────────────────────────────────────
  'string/unterminated': 'This string is never closed: the matching " is missing.',
  'string/invalid-escape':
    '\\{{ch}} is not a valid escape. JSON allows \\" \\\\ \\/ \\b \\f \\n \\r \\t and \\uXXXX.',
  'string/invalid-unicode-escape':
    '{{text}} is not a valid \\u escape. It needs exactly four hexadecimal digits, as in \\u00e9.',
  'string/control-character':
    'A raw U+{{hex}} control character inside a string. Anything below U+0020 has to be escaped — write it as \\u{{hex}}, or as \\n or \\t where that is what you meant.',
  'string/single-quoted': "This string is written with ' quotes. JSON strings are double-quoted.",
  'string/smart-quoted':
    'This string is written with typographic (“curly”) quotes. JSON strings use the straight " character.',

  // ── numbers ─────────────────────────────────────────────────────────────────────────
  'number/invalid-literal': '{{text}} is not a valid JSON number.',
  'number/leading-zero':
    '{{text}} has a leading zero. A JSON number starts with 0 only when the number is 0 itself.',
  'number/leading-plus':
    '{{text}} starts with "+". A JSON number may carry a leading "-", never a "+".',
  'number/leading-decimal-point':
    '{{text}} starts with ".". A JSON number needs at least one digit before the decimal point.',
  'number/trailing-decimal-point':
    '{{text}} ends with ".". A JSON number needs at least one digit after the decimal point.',
  'number/incomplete-exponent':
    '{{text}} has an exponent with no digits after it. An exponent needs at least one digit.',
  'number/hex-literal': '{{text}} is hexadecimal. JSON numbers are written in decimal only.',
  'number/non-finite':
    '{{text}} is not a JSON value: JSON has no way to write infinity or not-a-number.',
  'number/precision-loss':
    '{{text}} does not fit in a double, so a standard JSON parser reads it as {{parsed}}. Formatting keeps your digits, but the program you send this to may not.',

  // ── keys and foreign literals ───────────────────────────────────────────────────────
  'key/unquoted':
    'The property name {{text}} is not quoted. JSON property names are double-quoted strings, even when they look like identifiers.',
  'literal/js-undefined':
    'undefined is a JavaScript value, not a JSON one. Write null, or leave the member out.',
  'literal/python-keyword':
    '{{text}} is the Python spelling. JSON writes it as {{json}} — lowercase.',
  'jsonc/comment':
    'Comments are not part of JSON. This one is dropped when the document is repaired or formatted.',

  // ── semantics ───────────────────────────────────────────────────────────────────────
  'semantic/duplicate-key':
    'The property name {{key}} appears twice in this object. Which of the two a reader sees is undefined, so this is reported and never repaired automatically.',
  'semantic/deep-nesting':
    'This document nests {{depth}} levels deep, past the supported limit of {{limit}}. Analysis stops at the limit to keep the page responsive.',

  // ── encoding and internals ──────────────────────────────────────────────────────────
  'encoding/bom':
    'The document starts with a byte-order mark. It is invisible in an editor, and it makes a standard JSON parser fail on the very first character.',
  'internal/analysis-aborted':
    'Analysis stopped before it finished ({{reason}}). The document is not necessarily invalid.',

  // ── quick-fix labels ────────────────────────────────────────────────────────────────
  // Imperative, short enough for a button, and free of interpolation: the span already
  // tells the user where, and the diagnostic above it already says what.
  'fix.strip-bom': 'Remove the byte-order mark',
  'fix.remove-comment': 'Remove the comment',
  'fix.remove-trailing-comma': 'Remove the trailing comma',
  'fix.remove-stray-comma': 'Remove the stray comma',
  'fix.restore-missing-quote': 'Restore the missing quote',
  'fix.insert-missing-comma': 'Insert the missing comma',
  'fix.insert-missing-colon': 'Insert the missing colon',
  'fix.quote-unquoted-key': 'Quote the property name',
  'fix.requote-single-quoted': 'Use double quotes',
  'fix.normalize-smart-quotes': 'Replace the typographic quotes with straight ones',
  'fix.close-unterminated-string': 'Close the string',
  'fix.close-open-containers': 'Close the open objects and arrays',
  'fix.drop-extra-closers': 'Remove the unmatched closing brackets',
  'fix.escape-control-character': 'Escape the control character',
  'fix.normalize-js-literal': 'Replace undefined with null',
  'fix.normalize-python-literal': 'Use the JSON spelling',
  'fix.normalize-non-finite': 'Replace with null',
  'fix.fix-number-literal': 'Correct the number',
  'fix.drop-trailing-content': 'Remove the content after the document',
};

// ── rendering ─────────────────────────────────────────────────────────────────────────────

/**
 * i18next's interpolation syntax, with the optional inner padding it tolerates.
 *
 * Deliberately narrow: `{{name}}` only, no `{{obj.path}}`, no formatters (`{{n, number}}`).
 * The core supplies `Params`, which is `Record<string, string | number>` by type, so there
 * is nothing to reach into and nothing to format.
 */
const PLACEHOLDER = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

/**
 * The parameter names a template needs, in order of first appearance and de-duplicated.
 *
 * This is the machine-readable half of the contract between a check and this file: a check
 * that emits `number/leading-zero` must put a `literal` in `params`, and `en.test.ts` pins
 * the whole table so neither side can drift silently.
 */
export function messagePlaceholders(template: string): readonly string[] {
  const names: string[] = [];
  for (const match of template.matchAll(PLACEHOLDER)) {
    const name = match[1];
    if (name !== undefined && !names.includes(name)) names.push(name);
  }
  return names;
}

/** The first non-empty template for `code`, falling back through English to the code itself. */
function templateFor(code: MessageCode, catalog: MessageCatalog): string {
  const localized = catalog[code];
  if (typeof localized === 'string' && localized.length > 0) return localized;

  const english = EN_MESSAGES[code];
  if (typeof english === 'string' && english.length > 0) return english;

  // Unreachable through the type system; reachable through a hand-built locale object. A
  // raw code is ugly but greppable, which beats an empty bubble with no way to find it.
  return code;
}

/**
 * Render one code against a catalog.
 *
 * A placeholder with no matching parameter is left in place, verbatim. That is on purpose:
 * `{{text}}` on screen names the missing value and is greppable in a bug report, whereas
 * silently dropping it produces a grammatical sentence that is quietly wrong — the worse of
 * the two failures.
 */
export function renderMessage(code: MessageCode, catalog: MessageCatalog, params?: Params): string {
  const template = templateFor(code, catalog);
  if (params === undefined) return template;

  return template.replace(PLACEHOLDER, (whole, name: string) => {
    const value = params[name];
    if (value === undefined) return whole;
    return typeof value === 'number' ? String(value) : value;
  });
}

/**
 * Attach the rendered sentence to a diagnostic. The single point where the engine's data
 * meets a language — everything upstream of it is `code` + `span` + `params` (ADR-0008).
 *
 * `related` spans are left alone: a `RelatedSpan` carries its own `labelCode` and `params`,
 * and the UI renders each with {@link renderMessage} at the moment it draws it. Rendering
 * them eagerly here would put strings in a structure that is otherwise pure data.
 */
export function renderDiagnostic(d: Diagnostic, catalog: MessageCatalog): RenderedDiagnostic {
  return { ...d, message: renderMessage(d.code, catalog, d.params) };
}
